import React, { useState, useEffect } from 'react';
import { 
  MapPin, 
  Calculator, 
  Settings, 
  Search, 
  Truck, 
  Clock, 
  DollarSign,
  Navigation,
  AlertCircle,
  CheckCircle,
  Loader2,
  Save,
  X,
  Info
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface EnderecoCompleto {
  rua: string;
  numero_casa: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
}

interface ResultadoFrete {
  distancia: number;
  valorFrete: number;
  enderecoFormatado: string;
  tempoEstimado: string;
}

interface ConfiguracaoFrete {
  freteMinimo: number;
  valorPorKm: number;
  areaMaxima: number;
  enderecoBase: string;
  tempoPreparoPadrao?: number;
  tempoPorKm?: number;
}

const FretePage: React.FC = () => {
  // Estados para cálculo por endereço completo
  const [enderecoCompleto, setEnderecoCompleto] = useState<EnderecoCompleto>({
    rua: '',
    numero_casa: '',
    bairro: '',
    cidade: 'Passo Fundo',
    estado: 'RS',
    cep: ''
  });

  // Estados para cálculo apenas por CEP
  const [cepApenas, setCepApenas] = useState<string>('');

  // Estados para resultados
  const [resultadoCompleto, setResultadoCompleto] = useState<ResultadoFrete | null>(null);
  const [resultadoCep, setResultadoCep] = useState<ResultadoFrete | null>(null);

  // Estados de loading
  const [loadingCompleto, setLoadingCompleto] = useState<boolean>(false);
  const [loadingCep, setLoadingCep] = useState<boolean>(false);

  // Estados de erro
  const [erroCompleto, setErroCompleto] = useState<string | null>(null);
  const [erroCep, setErroCep] = useState<string | null>(null);

  // Estados para configurações
  const [configuracao, setConfiguracao] = useState<ConfiguracaoFrete>({
    freteMinimo: 5.0,
    valorPorKm: 1.0,
    areaMaxima: 25,
    enderecoBase: '',
    tempoPreparoPadrao: 30,
    tempoPorKm: 2.5
  });

  const [editandoConfig, setEditandoConfig] = useState<boolean>(false);
  const [loadingConfig, setLoadingConfig] = useState<boolean>(true);
  const [salvandoConfig, setSalvandoConfig] = useState<boolean>(false);
  const [abaAtiva, setAbaAtiva] = useState<'config' | 'calcular' | 'cep' | 'info'>('config');

  // Carregar configurações ao inicializar
  useEffect(() => {
    carregarConfiguracao();
  }, []);

  const carregarConfiguracao = async () => {
    setLoadingConfig(true);
    try {
      const response = await api.get('/api/frete/config');
      
      if (response.data.sucesso) {
        setConfiguracao({
          freteMinimo: response.data.dados.freteMinimo || 5.0,
          valorPorKm: response.data.dados.valorPorKm || 1.0,
          areaMaxima: response.data.dados.areaMaxima || 25,
          enderecoBase: response.data.dados.enderecoBase || '',
          tempoPreparoPadrao: response.data.dados.tempoPreparoPadrao || 30,
          tempoPorKm: response.data.dados.tempoPorKm || 2.5
        });
      } else {
        // Usar valores padrão se erro
        setConfiguracao({
          freteMinimo: 5.0,
          valorPorKm: 1.0,
          areaMaxima: 25,
          enderecoBase: '',
          tempoPreparoPadrao: 30,
          tempoPorKm: 2.5
        });
      }
    } catch (error: any) {
      console.error('Erro ao carregar configuração:', error);
      // Usar valores padrão em caso de erro
      setConfiguracao({
        freteMinimo: 5.0,
        valorPorKm: 1.0,
        areaMaxima: 25,
        enderecoBase: '',
        tempoPreparoPadrao: 30,
        tempoPorKm: 2.5
      });
      toast.error('Erro ao carregar configurações. Usando valores padrão.');
    } finally {
      setLoadingConfig(false);
    }
  };

  const calcularFreteCompleto = async () => {
    if (!enderecoCompleto.rua || !enderecoCompleto.numero_casa || !enderecoCompleto.bairro || !enderecoCompleto.cep) {
      setErroCompleto('Preencha todos os campos obrigatórios');
      return;
    }

    setLoadingCompleto(true);
    setErroCompleto(null);

    try {
      const response = await api.post('/api/frete/calcular', {
        ...enderecoCompleto,
        empresa_id: 1
      });
      
      if (response.data.sucesso) {
        setResultadoCompleto(response.data.dados);
      } else {
        setErroCompleto(response.data.erro || 'Erro ao calcular frete');
      }
    } catch (error: any) {
      console.error('Erro ao calcular frete completo:', error);
      setErroCompleto(error.response?.data?.erro || 'Erro ao calcular frete');
    } finally {
      setLoadingCompleto(false);
    }
  };

  const calcularFretePorCep = async () => {
    if (!cepApenas || cepApenas.length < 8) {
      setErroCep('Digite um CEP válido');
      return;
    }

    setLoadingCep(true);
    setErroCep(null);

    try {
      // Para cálculo apenas por CEP, vamos usar um endereço genérico
      const enderecoGenerico = {
        rua: 'Rua Principal',
        numero_casa: '1',
        bairro: 'Centro',
        cidade: 'Passo Fundo',
        estado: 'RS',
        cep: cepApenas
      };

      const response = await api.post('/api/frete/calcular', {
        ...enderecoGenerico,
        empresa_id: 1
      });
      
      if (response.data.sucesso) {
        setResultadoCep(response.data.dados);
      } else {
        setErroCep(response.data.erro || 'Erro ao calcular frete');
      }
    } catch (error: any) {
      console.error('Erro ao calcular frete por CEP:', error);
      setErroCep(error.response?.data?.erro || 'Erro ao calcular frete');
    } finally {
      setLoadingCep(false);
    }
  };

  const formatarCep = (valor: string) => {
    const numeros = valor.replace(/\D/g, '');
    if (numeros.length <= 5) {
      return numeros;
    } else {
      return `${numeros.slice(0, 5)}-${numeros.slice(5, 8)}`;
    }
  };

  const handleCepChange = (valor: string) => {
    const cepFormatado = formatarCep(valor);
    setCepApenas(cepFormatado);
  };

  const limparResultados = () => {
    setResultadoCompleto(null);
    setResultadoCep(null);
    setErroCompleto(null);
    setErroCep(null);
  };

  const salvarConfiguracao = async () => {
    // Validações
    if (configuracao.freteMinimo < 0) {
      toast.error('Frete mínimo deve ser maior ou igual a zero');
      return;
    }
    
    if (configuracao.valorPorKm < 0) {
      toast.error('Valor por KM deve ser maior ou igual a zero');
      return;
    }
    
    if (configuracao.areaMaxima <= 0) {
      toast.error('Área máxima deve ser maior que zero');
      return;
    }
    
    if (!configuracao.enderecoBase || configuracao.enderecoBase.trim() === '') {
      toast.error('Endereço base é obrigatório');
      return;
    }

    if (configuracao.tempoPreparoPadrao !== undefined && configuracao.tempoPreparoPadrao < 0) {
      toast.error('Tempo de preparo padrão deve ser maior ou igual a zero');
      return;
    }

    if (configuracao.tempoPorKm !== undefined && configuracao.tempoPorKm < 0) {
      toast.error('Tempo por KM deve ser maior ou igual a zero');
      return;
    }

    setSalvandoConfig(true);
    try {
      const response = await api.put('/api/frete/config', {
        freteMinimo: configuracao.freteMinimo,
        valorPorKm: configuracao.valorPorKm,
        areaMaxima: configuracao.areaMaxima,
        enderecoBase: configuracao.enderecoBase,
        tempoPreparoPadrao: configuracao.tempoPreparoPadrao,
        tempoPorKm: configuracao.tempoPorKm
      });
      
      if (response.data.sucesso) {
        toast.success('Configurações salvas com sucesso!');
        setEditandoConfig(false);
        // Recarregar para garantir sincronização
        await carregarConfiguracao();
      } else {
        toast.error(response.data.erro || 'Erro ao salvar configurações');
      }
    } catch (error: any) {
      console.error('Erro ao salvar configuração:', error);
      toast.error(error.response?.data?.erro || 'Erro ao salvar configurações');
    } finally {
      setSalvandoConfig(false);
    }
  };

  if (loadingConfig) {
    return (
      <div className="flex items-center justify-center h-48 md:h-64 lg:h-80">
        <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-4 lg:space-y-6 px-2 md:px-4 lg:px-0">
      {/* Header - Mobile: Stack, iPad/Desktop: Row */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-5 lg:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2 md:gap-3">
              <Truck className="h-6 w-6 md:h-7 md:w-7 lg:h-8 lg:w-8 text-blue-600" />
              Sistema de Frete
            </h1>
            <p className="text-xs md:text-sm lg:text-base text-gray-600 dark:text-gray-400 mt-1">
              Gerencie configurações e calcule valores de frete
            </p>
          </div>
        </div>
      </div>

      {/* Menu de Abas */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex flex-wrap -mb-px overflow-x-auto" aria-label="Tabs">
            <button
              onClick={() => setAbaAtiva('config')}
              className={`flex items-center gap-2 px-4 py-3 md:px-6 md:py-4 text-sm md:text-base font-medium border-b-2 transition-colors whitespace-nowrap ${
                abaAtiva === 'config'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Settings className="h-4 w-4 md:h-5 md:w-5" />
              <span className="hidden sm:inline">Configurações</span>
              <span className="sm:hidden">Config</span>
            </button>
            <button
              onClick={() => setAbaAtiva('calcular')}
              className={`flex items-center gap-2 px-4 py-3 md:px-6 md:py-4 text-sm md:text-base font-medium border-b-2 transition-colors whitespace-nowrap ${
                abaAtiva === 'calcular'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <MapPin className="h-4 w-4 md:h-5 md:w-5" />
              <span className="hidden sm:inline">Calcular Frete</span>
              <span className="sm:hidden">Calcular</span>
            </button>
            <button
              onClick={() => setAbaAtiva('cep')}
              className={`flex items-center gap-2 px-4 py-3 md:px-6 md:py-4 text-sm md:text-base font-medium border-b-2 transition-colors whitespace-nowrap ${
                abaAtiva === 'cep'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Search className="h-4 w-4 md:h-5 md:w-5" />
              <span className="hidden sm:inline">Buscar por CEP</span>
              <span className="sm:hidden">CEP</span>
            </button>
            <button
              onClick={() => setAbaAtiva('info')}
              className={`flex items-center gap-2 px-4 py-3 md:px-6 md:py-4 text-sm md:text-base font-medium border-b-2 transition-colors whitespace-nowrap ${
                abaAtiva === 'info'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Info className="h-4 w-4 md:h-5 md:w-5" />
              <span className="hidden sm:inline">Informações</span>
              <span className="sm:hidden">Info</span>
            </button>
          </nav>
        </div>
      </div>

      {/* Conteúdo das Abas */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-5 lg:p-6">
        {/* Aba: Configurações */}
        {abaAtiva === 'config' && (
          <div>
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <h2 className="text-base md:text-lg lg:text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Settings className="h-5 w-5 text-blue-600" />
                Configurações do Sistema de Frete
              </h2>
            </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                Frete Mínimo (R$) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={configuracao.freteMinimo}
                onChange={(e) => setConfiguracao(prev => ({ ...prev, freteMinimo: parseFloat(e.target.value) || 0 }))}
                className="w-full px-3 py-2 text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="5.00"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Valor fixo para distâncias até 5km</p>
            </div>
            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                Valor por KM (R$) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={configuracao.valorPorKm}
                onChange={(e) => setConfiguracao(prev => ({ ...prev, valorPorKm: parseFloat(e.target.value) || 0 }))}
                className="w-full px-3 py-2 text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="1.00"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Valor adicional por km acima de 5km</p>
            </div>
            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                Área Máxima (km) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                value={configuracao.areaMaxima}
                onChange={(e) => setConfiguracao(prev => ({ ...prev, areaMaxima: parseInt(e.target.value) || 25 }))}
                className="w-full px-3 py-2 text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="25"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Raio máximo de entrega em km</p>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                Endereço Base <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={configuracao.enderecoBase}
                onChange={(e) => setConfiguracao(prev => ({ ...prev, enderecoBase: e.target.value }))}
                className="w-full px-3 py-2 text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Rua, Número - Bairro, Cidade - Estado, CEP"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Endereço completo do restaurante para cálculo de distância</p>
            </div>
            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                Tempo de Preparo Padrão (min)
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={configuracao.tempoPreparoPadrao || 30}
                onChange={(e) => setConfiguracao(prev => ({ ...prev, tempoPreparoPadrao: parseInt(e.target.value) || 30 }))}
                className="w-full px-3 py-2 text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="30"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Tempo base de preparo em minutos</p>
            </div>
            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                Tempo por KM (min/km)
              </label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={configuracao.tempoPorKm || 2.5}
                onChange={(e) => setConfiguracao(prev => ({ ...prev, tempoPorKm: parseFloat(e.target.value) || 2.5 }))}
                className="w-full px-3 py-2 text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="2.5"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Tempo adicional por km de distância</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 md:gap-3 mt-4 md:mt-6">
            <button
              onClick={salvarConfiguracao}
              disabled={salvandoConfig}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm md:text-base font-medium"
            >
              {salvandoConfig ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Salvar Configurações
                </>
              )}
            </button>
            <button
              onClick={() => {
                setEditandoConfig(false);
                carregarConfiguracao(); // Recarregar para descartar alterações
              }}
              className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors text-sm md:text-base font-medium"
            >
              Cancelar
            </button>
          </div>
          </div>
        )}

        {/* Aba: Calcular Frete */}
        {abaAtiva === 'calcular' && (
          <div>
            <h2 className="text-base md:text-lg lg:text-xl font-semibold text-gray-900 dark:text-white mb-3 md:mb-4 flex items-center gap-2">
              <MapPin className="h-4 w-4 md:h-5 md:w-5 text-green-600" />
              Cálculo por Endereço Completo
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 mb-3 md:mb-4">
          <div>
            <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
              Rua <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={enderecoCompleto.rua}
              onChange={(e) => setEnderecoCompleto(prev => ({ ...prev, rua: e.target.value }))}
              className="w-full px-3 py-2 text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="Nome da rua"
            />
          </div>
          <div>
            <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
              Número <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={enderecoCompleto.numero_casa}
              onChange={(e) => setEnderecoCompleto(prev => ({ ...prev, numero_casa: e.target.value }))}
              className="w-full px-3 py-2 text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="Número"
            />
          </div>
          <div>
            <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
              Bairro <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={enderecoCompleto.bairro}
              onChange={(e) => setEnderecoCompleto(prev => ({ ...prev, bairro: e.target.value }))}
              className="w-full px-3 py-2 text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="Nome do bairro"
            />
          </div>
          <div>
            <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
              Cidade
            </label>
            <input
              type="text"
              value={enderecoCompleto.cidade}
              onChange={(e) => setEnderecoCompleto(prev => ({ ...prev, cidade: e.target.value }))}
              className="w-full px-3 py-2 text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="Cidade"
            />
          </div>
          <div>
            <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
              Estado
            </label>
            <input
              type="text"
              value={enderecoCompleto.estado}
              onChange={(e) => setEnderecoCompleto(prev => ({ ...prev, estado: e.target.value }))}
              className="w-full px-3 py-2 text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="Estado"
            />
          </div>
          <div>
            <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
              CEP <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={enderecoCompleto.cep}
              onChange={(e) => setEnderecoCompleto(prev => ({ ...prev, cep: formatarCep(e.target.value) }))}
              className="w-full px-3 py-2 text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="00000-000"
              maxLength={9}
            />
          </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
          <button
            onClick={calcularFreteCompleto}
            disabled={loadingCompleto}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm md:text-base font-medium w-full sm:w-auto"
          >
            {loadingCompleto ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Calculando...
              </>
            ) : (
              <>
                <Calculator className="h-4 w-4" />
                Calcular Frete
              </>
            )}
          </button>
          <button
            onClick={() => {
              setEnderecoCompleto({
                rua: '',
                numero_casa: '',
                bairro: '',
                cidade: 'Passo Fundo',
                estado: 'RS',
                cep: ''
              });
              setResultadoCompleto(null);
              setErroCompleto(null);
            }}
            className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors text-sm md:text-base font-medium w-full sm:w-auto"
          >
            Limpar
          </button>
            </div>

            {/* Resultado do cálculo completo */}
            {resultadoCompleto && (
          <div className="mt-3 md:mt-4 p-3 md:p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-center gap-2 mb-2 md:mb-3">
              <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-green-600" />
              <h3 className="font-semibold text-sm md:text-base text-green-800 dark:text-green-200">Frete Calculado</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 text-xs md:text-sm">
              <div>
                <p className="text-green-700 dark:text-green-300 mb-1">
                  <strong>Endereço:</strong> {resultadoCompleto.enderecoFormatado}
                </p>
                <p className="text-green-700 dark:text-green-300">
                  <strong>Distância:</strong> {resultadoCompleto.distancia} km
                </p>
              </div>
              <div>
                <p className="text-green-700 dark:text-green-300 mb-1">
                  <strong>Valor do Frete:</strong> <span className="text-lg md:text-xl font-bold">R$ {resultadoCompleto.valorFrete.toFixed(2)}</span>
                </p>
                <p className="text-green-700 dark:text-green-300">
                  <strong>Tempo Estimado:</strong> {resultadoCompleto.tempoEstimado}
                </p>
              </div>
            </div>
          </div>
            )}

            {erroCompleto && (
              <div className="mt-3 md:mt-4 p-3 md:p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 md:h-5 md:w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs md:text-sm text-red-700 dark:text-red-300">{erroCompleto}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Aba: Buscar por CEP */}
        {abaAtiva === 'cep' && (
          <div>
            <h2 className="text-base md:text-lg lg:text-xl font-semibold text-gray-900 dark:text-white mb-3 md:mb-4 flex items-center gap-2">
              <Search className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
              Cálculo Apenas por CEP
            </h2>
            
            <div className="mb-3 md:mb-4">
          <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
            CEP
          </label>
          <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
            <input
              type="text"
              value={cepApenas}
              onChange={(e) => handleCepChange(e.target.value)}
              className="flex-1 px-3 py-2 text-sm md:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="00000-000"
              maxLength={9}
            />
            <button
              onClick={calcularFretePorCep}
              disabled={loadingCep}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm md:text-base font-medium w-full sm:w-auto"
            >
              {loadingCep ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Calculando...
                </>
              ) : (
                <>
                  <Navigation className="h-4 w-4" />
                  Calcular
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 md:mt-2">
              * O cálculo por CEP usa um endereço genérico no centro do bairro
            </p>
            </div>

            <button
              onClick={() => {
                setCepApenas('');
                setResultadoCep(null);
                setErroCep(null);
              }}
              className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors text-sm md:text-base font-medium w-full sm:w-auto"
            >
              Limpar
            </button>

            {/* Resultado do cálculo por CEP */}
            {resultadoCep && (
          <div className="mt-3 md:mt-4 p-3 md:p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-center gap-2 mb-2 md:mb-3">
              <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
              <h3 className="font-semibold text-sm md:text-base text-blue-800 dark:text-blue-200">Frete Estimado por CEP</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 text-xs md:text-sm">
              <div>
                <p className="text-blue-700 dark:text-blue-300 mb-1">
                  <strong>CEP:</strong> {cepApenas}
                </p>
                <p className="text-blue-700 dark:text-blue-300">
                  <strong>Distância:</strong> {resultadoCep.distancia} km
                </p>
              </div>
              <div>
                <p className="text-blue-700 dark:text-blue-300 mb-1">
                  <strong>Valor do Frete:</strong> <span className="text-lg md:text-xl font-bold">R$ {resultadoCep.valorFrete.toFixed(2)}</span>
                </p>
                <p className="text-blue-700 dark:text-blue-300">
                  <strong>Tempo Estimado:</strong> {resultadoCep.tempoEstimado}
                </p>
              </div>
            </div>
            <div className="mt-2 md:mt-3 p-2 md:p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
              <p className="text-xs text-yellow-700 dark:text-yellow-300">
                <strong>Nota:</strong> Este é um valor estimado baseado no CEP. Para valores precisos, use o endereço completo.
              </p>
            </div>
          </div>
            )}

            {erroCep && (
              <div className="mt-3 md:mt-4 p-3 md:p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 md:h-5 md:w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs md:text-sm text-red-700 dark:text-red-300">{erroCep}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Aba: Informações */}
        {abaAtiva === 'info' && (
          <div>
            <h2 className="text-base md:text-lg lg:text-xl font-semibold text-gray-900 dark:text-white mb-3 md:mb-4 flex items-center gap-2">
              <Info className="h-4 w-4 md:h-5 md:w-5 text-gray-600" />
              Informações do Sistema
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          <div className="p-3 md:p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-4 w-4 md:h-5 md:w-5 text-green-600" />
              <h3 className="font-medium text-sm md:text-base text-gray-900 dark:text-white">Frete Mínimo</h3>
            </div>
            <p className="text-xl md:text-2xl font-bold text-green-600">R$ {Number(configuracao.freteMinimo || 0).toFixed(2)}</p>
            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1">Para distâncias até 5km</p>
          </div>
          
          <div className="p-3 md:p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Navigation className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
              <h3 className="font-medium text-sm md:text-base text-gray-900 dark:text-white">Valor por KM</h3>
            </div>
            <p className="text-xl md:text-2xl font-bold text-blue-600">R$ {Number(configuracao.valorPorKm || 0).toFixed(2)}</p>
            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1">Para distâncias acima de 5km</p>
          </div>
          
          <div className="p-3 md:p-4 bg-gray-50 dark:bg-gray-700 rounded-lg sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="h-4 w-4 md:h-5 md:w-5 text-orange-600" />
              <h3 className="font-medium text-sm md:text-base text-gray-900 dark:text-white">Área de Entrega</h3>
            </div>
            <p className="text-xl md:text-2xl font-bold text-orange-600">{configuracao.areaMaxima || 25} km</p>
            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1">Raio máximo de entrega</p>
            </div>
          </div>

          {configuracao.enderecoBase && (
            <div className="mt-3 md:mt-4 p-3 md:p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h3 className="font-medium text-xs md:text-sm text-blue-800 dark:text-blue-200 mb-1 md:mb-2">Endereço Base do Restaurante</h3>
              <p className="text-xs md:text-sm text-blue-700 dark:text-blue-300 break-words">{configuracao.enderecoBase}</p>
            </div>
          )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FretePage;
